import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  DockerSecurityFormData,
  DockerSecurityFormValues,
  DockerSecurityPolicyData,
  DOCKER_SECURITY_FORM_INITIAL_VALUES,
  DOCKER_SECURITY_INITIAL_VALUES,
} from './types';

/**
 * Type guard to check if data is DockerSecurityPolicyData
 */
function isDockerSecurityPolicyData(
  data: unknown
): data is DockerSecurityPolicyData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'allowBindMountsForRegularUsers' in data
  );
}

/**
 * Type guard to check if data is Partial<DockerSecurityPolicyData>
 */
function isPartialDockerSecurityPolicyData(
  data: unknown
): data is Partial<DockerSecurityPolicyData> {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}

/**
 * Deserializes a Docker Security policy payload into form values.
 * Used by both edit mode (loading existing policy) and template mode (applying template data).
 *
 * @param data - The policy payload data to deserialize
 * @param defaults - Default values to use when payload fields are missing
 * @returns Type-specific form values
 */
function deserializePayload(
  data: Partial<DockerSecurityPolicyData>,
  defaults: DockerSecurityFormData
): DockerSecurityFormData {
  return {
    allowBindMountsForRegularUsers:
      data.allowBindMountsForRegularUsers?.value ??
      defaults.allowBindMountsForRegularUsers,
    allowPrivilegedModeForRegularUsers:
      data.allowPrivilegedModeForRegularUsers?.value ??
      defaults.allowPrivilegedModeForRegularUsers,
    allowHostNamespaceForRegularUsers:
      data.allowHostNamespaceForRegularUsers?.value ??
      defaults.allowHostNamespaceForRegularUsers,
    allowDeviceMappingForRegularUsers:
      data.allowDeviceMappingForRegularUsers?.value ??
      defaults.allowDeviceMappingForRegularUsers,
    allowStackManagementForRegularUsers:
      data.allowStackManagementForRegularUsers?.value ??
      defaults.allowStackManagementForRegularUsers,
    allowContainerCapabilitiesForRegularUsers:
      data.allowContainerCapabilitiesForRegularUsers?.value ??
      defaults.allowContainerCapabilitiesForRegularUsers,
    allowSysctlSettingForRegularUsers:
      data.allowSysctlSettingForRegularUsers?.value ??
      defaults.allowSysctlSettingForRegularUsers,
  };
}

/**
 * Hook to get initial values for Docker Security Policy form.
 *
 * Returns complete form values including common fields (name, environmentGroupIds)
 * and type-specific fields.
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @param templateData - Optional template data to pre-fill form (for create from template)
 * @returns Complete form initial values
 */
export function useDockerSecurityInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): DockerSecurityFormValues {
  return useMemo(() => {
    const defaults = DOCKER_SECURITY_INITIAL_VALUES;

    // Template mode: apply template data over defaults
    if (
      !policy &&
      templateData &&
      isPartialDockerSecurityPolicyData(templateData)
    ) {
      const data = templateData;
      return {
        name: '',
        environmentGroupIds: [],
        ...deserializePayload(data, defaults),
      };
    }

    // Create mode: use defaults
    if (!policy) {
      return DOCKER_SECURITY_FORM_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isDockerSecurityPolicyData(data)) {
      return {
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        ...defaults,
      };
    }

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      ...deserializePayload(data, defaults),
    };
  }, [policy, templateData]);
}
